import { computed } from 'vue'
import type { LanguageSwitch } from './LanguageSwitch'
import type { SupportedLocale } from '~/types/SupportedLocale'
import { useLogger } from '~/composables/useLogger'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { useToast } from '~/composables/useToast'
import { supportedLocaleSchema } from '~/utils/supportedLocaleSchema'

export function useLanguageSwitch(): LanguageSwitch {
  const { $i18n } = useNuxtApp()
  const t = usePresentationTranslator()
  const toast = useToast()
  const logger = useLogger('LanguageSwitch')

  const locale = computed<SupportedLocale>(() => {
    const parsed = supportedLocaleSchema.safeParse($i18n.locale.value)
    return parsed.success ? parsed.data : 'en'
  })
  const targetLocale = computed<SupportedLocale>(() => (locale.value === 'es' ? 'en' : 'es'))
  const switchesToEnglish = computed(() => targetLocale.value === 'en')
  const setLocale = async (code: SupportedLocale): Promise<void> => {
    try {
      await $i18n.setLocale(code)
    } catch (error: unknown) {
      logger.error('setLocale', { code, error })
      toast.error(t('commerce.guestTopBar.languageChangeFailed'))
    }
  }

  return {
    locale,
    setLocale,
    toggleLocale: () => setLocale(targetLocale.value),
    switchAriaLabel: computed(() =>
      switchesToEnglish.value
        ? t('commerce.guestTopBar.switchToEnglishAriaLabel')
        : t('commerce.guestTopBar.switchToSpanishAriaLabel')
    ),
    switchLabel: computed(() =>
      switchesToEnglish.value
        ? t('commerce.guestTopBar.switchToEnglish')
        : t('commerce.guestTopBar.switchToSpanish')
    )
  }
}
